import { useEffect, useState } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { NAV, type NavGroup, type NavKey, ROUTE_FOR } from '@/roles/sv/nav'
import { VOC_SUB_NAV, vocSectionFromSearch } from '@/features/voc/sections'
import {
  isOmsPathActive,
  omsLabel,
  OMS_RAIL_NAV,
  OMS_SETTINGS_NAV,
  type OmsLocale,
} from '@/features/oms/nav'
import { useAuthStore } from '@/shared/store/authStore'
import {
  RAIL_COLLAPSE_THRESHOLD,
  RAIL_COLLAPSED_WIDTH,
  RAIL_WIDTH_DEFAULT,
  useLayoutStore,
} from '@/shared/store/layoutStore'
import { logoutLocally } from '@/features/auth/logout'
import { hasHostShell } from '@/shared/bridge/env'
import { cn } from '@/shared/utils/cn'
import { Badge, Button, Logo, NavIcon, OmsNavIcon, UiIcon } from '@/shared/ui'

const GROUP_LABEL: Record<NavGroup, string> = {
  home: '홈',
  qsc: 'QSC · 매장 품질관리',
  prm: 'PRM · 매장 운영관리',
  qa: 'QA · 납품업체 품질관리',
  as: 'AS',
  claim: '클레임 관리',
  more: '더보기',
}

const RAIL_FULL_LOGO_MIN_WIDTH = 224

interface RailProps {
  active: NavKey
  onNavigate?: () => void
  omsMode?: {
    activePath: string
    badges?: Record<string, number>
    locale: OmsLocale
    onClose: () => void
    onNavigate: (path: string, title: string) => void
  }
}

export function Rail({ active, onNavigate, omsMode }: RailProps) {
  const railWidth = useLayoutStore((state) => state.railWidth)
  const toggleRail = useLayoutStore((state) => state.toggleRail)
  const user = useAuthStore((state) => state.user)
  const location = useLocation()
  const navigate = useNavigate()
  const visible = NAV
  const hideHostLogout = hasHostShell()
  const collapsed = railWidth < RAIL_COLLAPSE_THRESHOLD
  const displayWidth = collapsed ? RAIL_COLLAPSED_WIDTH : RAIL_WIDTH_DEFAULT
  const logoCollapsed = collapsed || displayWidth < RAIL_FULL_LOGO_MIN_WIDTH
  const vocSection = vocSectionFromSearch(location.search)
  const [vocExpanded, setVocExpanded] = useState(active === 'voc')
  const omsLocale = omsMode?.locale ?? 'ko'
  const omsBackLabel =
    omsLocale === 'en' ? 'Back to QSC' : omsLocale === 'zh-TW' ? '返回 QSC' : 'QSC로 돌아가기'
  const omsMenuLabel =
    omsLocale === 'en' ? 'OMS Menu' : omsLocale === 'zh-TW' ? 'OMS 選單' : 'OMS 메뉴'
  const omsSettingsLabel = omsLocale === 'en' ? 'Settings' : omsLocale === 'zh-TW' ? '設定' : '설정'

  useEffect(() => {
    if (active === 'voc') setVocExpanded(true)
  }, [active])

  const toggleVocMenu = () => {
    const nextExpanded = active === 'voc' ? !vocExpanded : true
    setVocExpanded(nextExpanded)
    if (nextExpanded) {
      onNavigate?.()
      navigate(VOC_SUB_NAV[0].path)
    }
  }

  return (
    <aside
      className="relative z-20 hidden h-full min-h-0 shrink-0 flex-col border-r border-border-subtle bg-surface transition-[width] duration-base lg:flex"
      style={{ width: displayWidth }}
    >
      <div
        className={cn(
          'flex h-18 items-center gap-3 border-b border-border-subtle px-4',
          logoCollapsed && 'justify-center px-2',
        )}
      >
        <Logo collapsed={logoCollapsed} />
        {!logoCollapsed && (
          <Button
            variant="ghost"
            size="icon"
            className="ml-auto"
            onClick={toggleRail}
            aria-label="메뉴 접기"
          >
            <ChevronLeft aria-hidden className="size-4" />
          </Button>
        )}
      </div>
      {collapsed && (
        <div className="flex justify-center border-b border-border-subtle py-2">
          <Button variant="ghost" size="icon" onClick={toggleRail} aria-label="메뉴 펼치기">
            <ChevronRight aria-hidden className="size-4" />
          </Button>
        </div>
      )}

      <nav className="flex-1 overflow-y-auto px-3 py-4">
        {omsMode ? (
          <div className="flex flex-col gap-1">
            <button
              type="button"
              onClick={omsMode.onClose}
              className={cn(
                'group flex h-11 items-center gap-3 rounded-md px-3 text-preset-label text-text-muted transition hover:bg-gray-50 hover:text-text focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2',
                collapsed && 'justify-center px-0',
              )}
              title={collapsed ? omsBackLabel : undefined}
            >
              <UiIcon.ChevronLeft aria-hidden className="size-5 shrink-0" />
              {!collapsed && <span className="truncate">{omsBackLabel}</span>}
            </button>
            <div className="my-2 border-t border-border-subtle" />
            {!collapsed && (
              <p className="mb-2 px-3 text-preset-helper font-bold text-text-subtle">
                {omsMenuLabel}
              </p>
            )}
            {OMS_RAIL_NAV.map((item) => {
              const selected = isOmsPathActive(omsMode.activePath, item.path)
              const badgeCount = omsMode.badges?.[item.k] ?? 0
              const label = omsLabel(item, omsLocale)
              return (
                <button
                  key={item.k}
                  type="button"
                  onClick={() => omsMode.onNavigate(item.path, label)}
                  className={cn(
                    'group flex h-11 items-center gap-3 rounded-md px-3 text-preset-label transition focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2',
                    collapsed && 'justify-center px-0',
                    selected
                      ? 'bg-brand-soft text-brand'
                      : 'text-text-muted hover:bg-gray-50 hover:text-text',
                  )}
                  title={collapsed ? label : undefined}
                >
                  <span className="relative shrink-0">
                    <OmsNavIcon name={item.icon} className="size-5" />
                    <Badge
                      count={badgeCount}
                      className="absolute -right-[10px] -top-[9px] h-[16px] min-w-[16px] px-[4px] text-[9px] ring-2 ring-surface"
                    />
                  </span>
                  {!collapsed && <span className="truncate">{label}</span>}
                </button>
              )
            })}
            <div className="my-2 border-t border-border-subtle" />
            {!collapsed && (
              <p className="mb-2 px-3 text-preset-helper font-bold text-text-subtle">
                {omsSettingsLabel}
              </p>
            )}
            {OMS_SETTINGS_NAV.map((item) => {
              const selected = isOmsPathActive(omsMode.activePath, item.path)
              const label = omsLabel(item, omsLocale)
              return (
                <button
                  key={item.k}
                  type="button"
                  onClick={() => {
                    const path = item.action
                      ? `${item.path}${item.path.includes('?') ? '&' : '?'}ts=${Date.now()}`
                      : item.path
                    omsMode.onNavigate(path, label)
                  }}
                  className={cn(
                    'group flex h-11 items-center gap-3 rounded-md px-3 text-preset-label transition focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2',
                    collapsed && 'justify-center px-0',
                    selected
                      ? 'bg-brand-soft text-brand'
                      : 'text-text-muted hover:bg-gray-50 hover:text-text',
                  )}
                  title={collapsed ? label : undefined}
                >
                  <OmsNavIcon name={item.icon} className="size-5 shrink-0" />
                  {!collapsed && <span className="truncate">{label}</span>}
                </button>
              )
            })}
          </div>
        ) : (
          (['home', 'qsc', 'prm', 'qa', 'as', 'claim', 'more'] as NavGroup[]).map((group) => {
            const items = visible.filter((item) => item.group === group)
            if (items.length === 0) return null
            return (
              <div key={group} className="mb-5 last:mb-0">
                {!collapsed && (
                  <p className="mb-2 px-3 text-preset-helper font-bold text-text-subtle">
                    {GROUP_LABEL[group]}
                  </p>
                )}
                <div className="flex flex-col gap-1">
                  {items.map((item) => {
                    if (item.k === 'voc' && !collapsed) {
                      const selected = active === item.k
                      return (
                        <div key={item.k}>
                          <button
                            type="button"
                            onClick={toggleVocMenu}
                            className={cn(
                              'group flex h-11 w-full items-center gap-3 rounded-md px-3 text-preset-label transition focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2',
                              selected
                                ? 'bg-brand-soft text-brand'
                                : 'text-text-muted hover:bg-gray-50 hover:text-text',
                            )}
                            aria-expanded={vocExpanded}
                          >
                            <NavIcon name={item.icon} className="size-5 shrink-0" />
                            <span className="min-w-0 flex-1 truncate text-left">{item.ko}</span>
                            <ChevronRight
                              aria-hidden
                              className={cn(
                                'size-4 shrink-0 transition',
                                vocExpanded && 'rotate-90',
                              )}
                            />
                          </button>
                          <div
                            className={cn(
                              'grid transition-all duration-300 ease-out',
                              vocExpanded
                                ? 'grid-rows-[1fr] opacity-100'
                                : 'grid-rows-[0fr] opacity-0',
                            )}
                          >
                            <div className="overflow-hidden pl-8 pr-1 pt-1">
                              <div className="flex flex-col gap-1 border-l border-border-subtle pl-2">
                                {VOC_SUB_NAV.map((child) => (
                                  <NavLink
                                    key={child.k}
                                    to={child.path}
                                    onClick={onNavigate}
                                    className={cn(
                                      'rounded-md px-3 py-2 text-preset-helper font-bold transition focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2',
                                      active === 'voc' && vocSection === child.k
                                        ? 'bg-brand text-white shadow-card'
                                        : 'text-text-muted hover:bg-gray-50 hover:text-text',
                                    )}
                                  >
                                    {child.ko}
                                  </NavLink>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    }

                    return (
                      <NavLink
                        key={item.k}
                        to={item.k === 'voc' ? VOC_SUB_NAV[0].path : ROUTE_FOR[item.k]}
                        onClick={onNavigate}
                        className={({ isActive }) => {
                          const selected = active ? active === item.k : isActive
                          return cn(
                            'group flex h-11 items-center gap-3 rounded-md px-3 text-preset-label transition focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2',
                            collapsed && 'justify-center px-0',
                            selected
                              ? 'bg-brand-soft text-brand'
                              : 'text-text-muted hover:bg-gray-50 hover:text-text',
                          )
                        }}
                        title={collapsed ? item.ko : undefined}
                      >
                        <NavIcon name={item.icon} className="size-5 shrink-0" />
                        {!collapsed && <span className="truncate">{item.ko}</span>}
                      </NavLink>
                    )
                  })}
                </div>
              </div>
            )
          })
        )}
      </nav>

      <div className="border-t border-border-subtle p-3">
        {!collapsed && (
          <div className="mb-2 flex items-center gap-3 rounded-md px-3 py-2">
            <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-brand-soft text-preset-label font-extrabold text-brand">
              {user?.userNm?.[0] ?? 'U'}
            </div>
            <div className="min-w-0">
              <p className="truncate text-preset-label text-text">{user?.userNm ?? '사용자'} 님</p>
              <p className="truncate text-preset-helper text-text-subtle">SV / 슈퍼바이저</p>
            </div>
          </div>
        )}
        {!hideHostLogout && (
          <Button
            variant={omsMode ? 'danger' : 'ghost'}
            size={collapsed ? 'icon' : 'md'}
            className={cn(
              'w-full justify-start',
              omsMode ? 'shadow-none' : 'text-text-muted hover:text-danger',
              collapsed && 'justify-center',
            )}
            onClick={() => logoutLocally()}
            aria-label="로그아웃"
          >
            <UiIcon.LogOut aria-hidden className="size-4" />
            {!collapsed && <span>로그아웃</span>}
          </Button>
        )}
      </div>
    </aside>
  )
}

export function pageTitleFor(active: NavKey, search = '') {
  if (active === 'voc') {
    return VOC_SUB_NAV.find((item) => item.k === vocSectionFromSearch(search))?.ko ?? 'VOC'
  }
  return NAV.find((item) => item.k === active)?.ko ?? ''
}
